import { usePermissions, useTranslate } from 'react-admin';
import CustomList from '@/components/global/list/CustomList.jsx';
import { PeriodFilter, ReferenceArrayFilter } from '@/components/global/filters/filters.jsx';
import { useMemo } from 'react';
import { ProductCountAnalytics } from '@/components/analytics/ProductCountAnalytics.jsx';
import { TransactionSummaryAnalytics } from '@/components/analytics/TransactionSummaryAnalytics.jsx';
import { ProductRanking } from '@/components/analytics/ProductRanking.jsx';
import { TagRanking } from '@/components/analytics/TagRanking.jsx';
import { AnalyticsListActions } from '@/components/analytics/common/AnalyticsListActions';
import { SubContent } from '@/components/analytics/common/SubContent.jsx';
import CustomSelectArrayInput from '@/components/global/inputs/CustomSelectArrayInput.jsx';
import { provinceChoices } from '@/components/global/options/selectOptions';
import { useTheme, useMediaQuery } from '@mui/material';

const Filters = () => {
    const { permissions } = usePermissions();
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const baseFilters = useMemo(
        () => [
            <PeriodFilter key='period' source='period' alwaysOn={true} />,
            <ReferenceArrayFilter
                key='collectionPointId_in'
                source='collectionPointId_in'
                label={translate('common.collectionPoint')}
                optionText={choice => choice.customId}
                reference='collection-point'
                searchFields={['customId']}
                alwaysOn={isMobile}
                displayDeleteIcon={true}
                forceDesktopOnMobile={true}
            />,
            <ReferenceArrayFilter
                key='city'
                source='collectionPoint->address->city_in'
                reference='city'
                label={translate('resources.campaign.labels.cities')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <CustomSelectArrayInput
                key='province'
                source='collectionPoint->address->province_in'
                choices={provinceChoices(translate)}
                label={translate('resources.collectionPoint.form.voivodeship')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
        ],
        [isMobile, translate],
    );

    const adminFilters = useMemo(
        () => [
            ...baseFilters,
            <ReferenceArrayFilter
                key='groupId_in'
                source='groupId_in'
                reference='device-group'
                label={translate('resources.device.fields.group')}
                displayDeleteIcon={true}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='contractorId_in'
                source='contractorId_in'
                reference='contractor'
                label={translate('common.contractor')}
                displayDeleteIcon={true}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
        ],
        [baseFilters, isMobile, translate],
    );

    return permissions?.isAdmin || permissions?.isBok ? adminFilters : baseFilters;
};

export const Dashboard = () => {
    const translate = useTranslate();

    return (
        <CustomList
            title={translate('menu.productAnalytics')}
            filters={Filters()}
            filterDefaultValues={{ period: '30' }}
            component='div'
            disablePagination={true}
            actions={<AnalyticsListActions />}
        >
            <TransactionSummaryAnalytics id='device' />
            <ProductCountAnalytics />
            <SubContent first={<ProductRanking />} second={<TagRanking />} />
        </CustomList>
    );
};
